import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";
import SbuxStyledTableCell from "../../../../../components/SbuxTable/SbuxStyledTableCell";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import ExpandableRow from "../../common/ExpandableRow";
import MediaMgtDeleteDialog from "../../common/MediaMgtDeleteDialog";
import SbuxStackSnackbar from "../../../../../components/SbuxStackSnackbar";
import { Box, Button } from "@mui/material";
import { ellipse, getTitleForEllipse } from "../../../../../utils/stringUtil";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { getLastUpdated, getMediaMgtToolTipMsg } from "../../../utils";
import { SNACK_BARS_SEVERITY_ERROR } from "../../../../../constants";
import { styled } from "@mui/material/styles";
import styles from "../../styles";
import { getMediaShowEdit } from "../../../../../services/mediaMgt";
import useCss from "../../../../../hooks/useCss";
import {
  getMediaContentSelector,
  getMediaExpandCollapseInfoSelector,
} from "../../../../../selectors/mediaMgtSelector";
import { getClosureMsgDataSelector } from "../../../../../selectors/closureMsgSelector";
import { getTemporaryMsgDataSelector } from "../../../../../selectors/temporaryMsgSelector";
import { getTimezoneSelector } from "../../../../../selectors/settingsSelector";
import type { MediaExpandCollapseInfo } from "../../common/types";

interface MediaRow {
  mediaId?: string;
  highLight?: boolean;
  instanceId?: string;
  languageCode?: string;
  language?: string;
  filePath?: string;
  fileName?: string;
  type?: string;
  promptName?: string;
  pname?: string;
  [key: string]: unknown;
}

interface Props {
  row: MediaRow;
}

const MediaMgtRow = ({ row }: Props) => {
  const classes = useCss(styles);
  const [, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [mediaFileInUseText, setMediaFileInUseText] = useState("");

  const mediaContent = useAppSelector(getMediaContentSelector);
  const mediaExpandCollapseInfo = useAppSelector(
    getMediaExpandCollapseInfoSelector,
  ) as MediaExpandCollapseInfo | null;
  const closureMsgData = useAppSelector(getClosureMsgDataSelector) ?? [];
  const temporaryMsgData = useAppSelector(getTemporaryMsgDataSelector) ?? [];
  const timezone = useAppSelector(getTimezoneSelector);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (mediaExpandCollapseInfo?.isExpanded) {
      const result = mediaExpandCollapseInfo.isExpanded
        ? getMediaMgtToolTipMsg(
            row,
            closureMsgData as Record<string, unknown>[],
            temporaryMsgData as Record<string, unknown>[],
          )
        : "";
      setMediaFileInUseText(result);
    }
  }, [mediaExpandCollapseInfo]);

  const CustomWidthTooltip = styled(
    ({ className, ...props }: React.ComponentProps<typeof Tooltip>) => (
      <Tooltip {...props} classes={{ popper: className }} />
    ),
  )({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 450,
    },
  });

  const handleEdit = (rowData: MediaRow) => {
    setIsEditOpen(true);
    dispatch(getMediaShowEdit({ data: rowData as Record<string, unknown> }));
  };
  const handleDelete = () => {
    setIsDeleteOpen((prevState) => !prevState);
  };

  const handleClose = () => {
    // clickaway handler
  };

  return (
    <>
      <ExpandableRow
        key={row.pname}
        mediaId={row.mediaId ?? ""}
        isHighlighted={row.highLight}
        mediaObj={{
          instanceId: row.instanceId ?? "",
          languageCode: row.languageCode ?? "",
          language: row.language ?? "",
          fileName: encodeURIComponent(row.filePath ?? ""),
          mediaId: row.mediaId ?? "",
          type: row.type ?? "",
        }}
        expandComponent={
          <SbuxStyledTableCell colSpan={5}>
            <Box className={classes.megiaMgtRowContainer}>
              <audio
                style={{
                  height: 26,
                  width: 270,
                  marginRight: 4,
                }}
                controls
                title={row.fileName}
              >
                <source src={mediaContent || undefined} type="audio/wav" />
              </audio>
              <Button
                disabled={row.type === "System"}
                onClick={() => handleEdit(row)}
                variant="contained"
                size="medium"
              >
                <SbuxTranslate>{`Edit`}</SbuxTranslate>
              </Button>

              {mediaFileInUseText === "" && (
                <Button
                  disabled={row.type === "System"}
                  onClick={handleDelete}
                  variant="contained"
                  size="medium"
                  color={"complementary" as "primary"}
                >
                  <SbuxTranslate>{`Delete`}</SbuxTranslate>
                </Button>
              )}

              {mediaFileInUseText !== "" && (
                <CustomWidthTooltip
                  disableFocusListener
                  disableTouchListener
                  placement="bottom"
                  title={
                    <Box>
                      <SbuxStackSnackbar
                        width={450}
                        open={true}
                        message={mediaFileInUseText}
                        severity={SNACK_BARS_SEVERITY_ERROR}
                        handleClose={handleClose}
                      />
                    </Box>
                  }
                >
                  <Box>
                    <Button
                      disabled
                      variant="contained"
                      size="medium"
                      color={"complementary" as "primary"}
                    >
                      <span>
                        <SbuxTranslate>{`Delete`}</SbuxTranslate>
                      </span>
                    </Button>
                  </Box>
                </CustomWidthTooltip>
              )}
            </Box>
          </SbuxStyledTableCell>
        }
      >
        <SbuxStyledTableCell
          className={classes.cellFocus}
          title={getTitleForEllipse(row.promptName)}
        >
          {ellipse(
            row["promptName"] as string | null | undefined,
            Number(process.env.REACT_APP_COLUMN_LENGTH_BEFORE_ELLIPSIS),
          )}
        </SbuxStyledTableCell>
        <SbuxStyledTableCell className={classes.cellFocus}>
          {row["type"] as string}
        </SbuxStyledTableCell>
        <SbuxStyledTableCell className={classes.cellFocus}>
          {row["language"] as string}
        </SbuxStyledTableCell>
        <SbuxStyledTableCell className={classes.cellFocus}>
          {getLastUpdated(row as Record<string, unknown>, timezone)}
        </SbuxStyledTableCell>
      </ExpandableRow>
      {isDeleteOpen && (
        <MediaMgtDeleteDialog
          row={row as Record<string, unknown>}
          title={"Delete Media Management"}
          name={row.promptName ?? ""}
          open={isDeleteOpen}
          toggleOpen={handleDelete}
        />
      )}
    </>
  );
};

export default React.memo(MediaMgtRow);
